import { useCallback, useEffect, useId, useMemo } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';

import { useStartAPetitionPageState } from '#src/app/pages/startAPetitionV2/pageContext';
import { useFocusFallbackRef } from '#src/app/shared/hooks/focus';

import type { DecisionMaker, DecisionMakerContainerStatus, DecisionMakerSectionTracking } from '../../../../types';
import { decisionMakerFocusFallback } from '../../decisionMakerFocusFallback';

import { getSectionTracking } from './utils';

type UseDecisionMakerContainerReturn = ModelHookResult<
	{
		status: DecisionMakerContainerStatus;
		/** The recommendations the starter hasn't removed. */
		decisionMakers: readonly DecisionMaker[];
		/** How many inference found, which the intro counts: removing some doesn't change it. */
		foundCount: number;
		/** The id of the section heading, which names the carousel. */
		headingId: string;
		/** The section, where focus lands somewhere useful when a Remove unmounts the focused button. */
		focusFallbackRef: ReturnType<typeof useFocusFallbackRef>;
		/** Only on edit: preview is read-only. */
		removable: boolean;
		/** Only on edit: preview matches the petition page, which has no intro. */
		showIntro: boolean;
		/** What the section's view and impression send; unset while hidden, or until the URL reaches the step. */
		sectionTracking: DecisionMakerSectionTracking | undefined;
	},
	{
		removeDecisionMaker: (canonicalPetitionTargetId: string) => void;
	}
>;

/** What the decision-maker container shows on the step, and removing a decision maker on edit. */
export function useDecisionMakerContainer(): UseDecisionMakerContainerReturn {
	const { data, actions } = useStartAPetitionPageState();
	const track = useTracking();

	const { eligible, recommendations, loadingShown, carouselRenderedOnEdit } =
		data.draft?.metadata?.decisionMakers ?? {};
	const selectedDecisionMakerIds = data.draft?.petitionDraft?.selectedDecisionMakerIds;

	const decisionMakers = useMemo(
		() =>
			(recommendations ?? []).filter(({ canonicalPetitionTargetId }) =>
				(selectedDecisionMakerIds ?? []).includes(canonicalPetitionTargetId),
			),
		[recommendations, selectedDecisionMakerIds],
	);

	const isPreview = data.normalizedStep === 'preview';
	const running = actions.matchesOperation('inferDecisionMakers', 'running');
	const foundCount = recommendations?.length ?? 0;
	// the machine moves to the next step a render before the URL does, so that first render still has the last
	// step's name: tracking waits until the machine is on the step the URL says (the section is only on these two)
	const stepSettled = !!actions.matches(isPreview ? 'preview' : 'edit');

	const status = useMemo((): DecisionMakerContainerStatus => {
		if (!eligible) return 'hidden';
		// preview is read-only and only repeats what edit showed: no loading or empty message
		if (isPreview) return carouselRenderedOnEdit && decisionMakers.length > 0 ? 'done' : 'hidden';
		if (running) return 'loading';
		if (decisionMakers.length > 0) return 'done';
		// the starter removed them all, or saw loading and inference then failed or found none
		if (foundCount > 0 || loadingShown) return 'empty';
		// inference found none before the starter ever saw it loading
		return 'hidden';
	}, [eligible, isPreview, carouselRenderedOnEdit, running, decisionMakers.length, foundCount, loadingShown]);

	// kept on the draft so leaving and returning to edit still shows the empty message
	useEffect(() => {
		if (status === 'loading' && !loadingShown) {
			actions.send({ type: 'patch', draft: { metadata: { decisionMakers: { loadingShown: true } } } });
		}
	}, [actions, status, loadingShown]);

	// kept on the draft so preview knows the cards rendered on edit, whether or not the starter scrolled to them
	useEffect(() => {
		if (!isPreview && status === 'done' && !carouselRenderedOnEdit) {
			actions.send({ type: 'patch', draft: { metadata: { decisionMakers: { carouselRenderedOnEdit: true } } } });
		}
	}, [actions, carouselRenderedOnEdit, isPreview, status]);

	const removeDecisionMaker = useCallback(
		(canonicalPetitionTargetId: string) => {
			actions.send({
				type: 'patch',
				draft: {
					petitionDraft: {
						selectedDecisionMakerIds: (selectedDecisionMakerIds ?? []).filter((id) => id !== canonicalPetitionTargetId),
					},
				},
			});
			void track('sap_dm_remove_click', {
				step: data.normalizedStep,
				decision_maker_id: canonicalPetitionTargetId,
				dm_count: decisionMakers.length - 1,
			});
		},
		[actions, data.normalizedStep, decisionMakers.length, selectedDecisionMakerIds, track],
	);

	const headingId = useId();
	const focusFallbackRef = useFocusFallbackRef(decisionMakerFocusFallback);

	return {
		data: {
			status,
			decisionMakers,
			foundCount,
			headingId,
			focusFallbackRef,
			removable: !isPreview,
			showIntro: !isPreview,
			sectionTracking: stepSettled
				? getSectionTracking({
						status,
						step: data.normalizedStep,
						dmCount: decisionMakers.length,
						foundCount,
						// `null` or `[]` when DMI found none
						dmiSettled: recommendations !== undefined,
					})
				: undefined,
		},
		actions: { removeDecisionMaker },
	};
}
